import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../config";
import { Box, Button, Typography,  Chip, Paper } from "@mui/material";
import {
  createTheme,
  ThemeProvider,
  styled,
  keyframes,
  alpha,
} from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import PublicIcon from "@mui/icons-material/Public";

// ─── Theme ────────────────────────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#2563EB", light: "#DBEAFE", dark: "#1D4ED8" },
    background: { default: "#F0F2F8", paper: "#FFFFFF" },
    text: { primary: "#0F1117", secondary: "#4B5563", disabled: "#9CA3AF" },
  },
  typography: {
    fontFamily: "'Sora', 'Segoe UI', sans-serif",
    h2: { fontFamily: "'DM Serif Display', Georgia, serif", fontWeight: 400 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
  },
});

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const cardEntrance = keyframes`
  from { opacity: 0; transform: translateY(18px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;
const skeletonPulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.4; }
`;


// ─── Styled Components ────────────────────────────────────────────────────────
const PageWrapper = styled(Box)(() => ({
  minHeight: "100vh",
  background: "linear-gradient(145deg, #EEF2FF 0%, #F0F2F8 40%, #E8EDF8 100%)",
  padding: "28px 20px 60px",
  fontFamily: "'Sora', sans-serif",
  "&::before": {
    content: '""',
    position: "fixed",
    top: -120,
    right: -120,
    width: 480,
    height: 480,
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(37,99,235,0.06) 0%, transparent 70%)",
    pointerEvents: "none",
  },
}));

const ContentBox = styled(Box)({ maxWidth: 860, margin: "0 auto" });

const BackBtn = styled(Button)(({ theme }) => ({
  borderRadius: 40,
  padding: "7px 18px 7px 12px",
  fontSize: 13,
  fontWeight: 600,
  textTransform: "none",
  color: theme.palette.text.secondary,
  border: `1px solid ${alpha("#000", 0.12)}`,
  background: "#fff",
  transition: "all 0.2s ease",
  "&:hover": {
    background: alpha(theme.palette.primary.main, 0.05),
    borderColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
    transform: "translateX(-2px)",
  },
}));

const SectionCard = styled(Paper)(({ theme }) => ({
  borderRadius: 16,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
  boxShadow: "0 1px 3px rgba(0,0,0,0.05), 0 8px 24px rgba(0,0,0,0.04)",
  padding: "24px 28px",
  animation: `${fadeUp} 0.5s ease both`,
  animationDelay: "80ms",
  transition: "box-shadow 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 12px rgba(37,99,235,0.06), 0 12px 32px rgba(0,0,0,0.06)",
  },
}));

const CardLabel = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.9px",
  color: theme.palette.text.disabled,
  marginBottom: 20,
  display: "flex",
  alignItems: "center",
  gap: 8,
  "&::before": {
    content: '""',
    display: "inline-block",
    width: 3,
    height: 14,
    background: theme.palette.primary.main,
    borderRadius: 2,
  },
}));

const CountryCard = styled(Paper, {
  shouldForwardProp: (p) => p !== "itemdelay",
})(({ theme, itemdelay }) => ({
  borderRadius: 14,
  border: `1px solid ${alpha("#000", 0.07)}`,
  boxShadow: "0 1px 4px rgba(0,0,0,0.04), 0 4px 14px rgba(0,0,0,0.03)",
  padding: "16px 20px",
  display: "flex",
  alignItems: "center",
  gap: 16,
  cursor: "pointer",
  marginBottom: 12,
  animation: `${cardEntrance} 0.45s ease both`,
  animationDelay: `${itemdelay ?? 0}ms`,
  transition: "all 0.22s ease",
  "&:last-child": { marginBottom: 0 },
  "&:hover": {
    boxShadow: "0 6px 20px rgba(37,99,235,0.09), 0 10px 28px rgba(0,0,0,0.06)",
    transform: "translateY(-2px)",
    borderColor: alpha("#2563EB", 0.22),
    "& .arrow-icon": { transform: "translateX(4px)", opacity: 1 },
    "& .flag-box": {
      transform: "scale(1.06)",
      boxShadow: "0 4px 12px rgba(0,0,0,0.14)",
    },
  },
}));

const FlagBox = styled(Box)(() => ({
  width: 52,
  height: 52,
  borderRadius: 13,
  flexShrink: 0,
  overflow: "hidden",
  border: `1px solid ${alpha("#000", 0.08)}`,
  background: alpha("#2563EB", 0.06),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "all 0.22s ease",
  boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
}));

const SkeletonCard = styled(Box)(() => ({
  borderRadius: 14,
  border: `1px solid ${alpha("#000", 0.06)}`,
  padding: "16px 20px",
  display: "flex",
  alignItems: "center",
  gap: 16,
  background: "#fff",
  marginBottom: 12,
  animation: `${skeletonPulse} 1.5s ease-in-out infinite`,
}));

const SkeletonBlock = ({ w = "100%", h = 14, radius = 6 }) => (
  <Box
    sx={{
      width: w,
      height: h,
      borderRadius: radius,
      background: alpha("#000", 0.07),
      flexShrink: 0,
    }}
  />
);



// ─── Main Component ───────────────────────────────────────────────────────────
const AdminExamCountryPage = ({page}) => {
  const [countries, setCountries] = useState([]);
  const [fetching, setFetching] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    setFetching(true);
    try {
      const response = await axios.get(`${API_URL}/admin/countries/`);
      setCountries(response.data);
    } catch (error) {
      console.error("Error fetching countries:", error);
    } finally {
      setFetching(false);
    }
  };

  const navigateToExam = (countryId) => navigate(`/${page}/${countryId}`);

  return (
    <ThemeProvider theme={theme}>
      <link
        href="https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=DM+Serif+Display&display=swap"
        rel="stylesheet"
      />

      <PageWrapper>
        <ContentBox>
          {/* ── Header ── */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 4,
              animation: `${fadeUp} 0.4s ease both`,
              flexWrap: "wrap",
            }}
          >
            <BackBtn
              startIcon={<ArrowBackIcon sx={{ fontSize: "15px !important" }} />}
              onClick={() => navigate(-1)}
            >
              Back
            </BackBtn>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: 24, sm: 30 },
                  color: "text.primary",
                  lineHeight: 1.1,
                }}
              >
                Exam Countries
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: "text.disabled",
                  mt: 0.3,
                  fontFamily: "'Sora', sans-serif",
                }}
              >
                Select a country to manage its exams
              </Typography>
            </Box>

            {!fetching && (
              <Chip
                label={`${countries.length} countr${
                  countries.length !== 1 ? "ies" : "y"
                }`}
                size="small"
                sx={{
                  ml: "auto",
                  fontFamily: "'Sora', sans-serif",
                  fontWeight: 700,
                  fontSize: 11,
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                  background: alpha("#2563EB", 0.1),
                  color: "#2563EB",
                  border: `1px solid ${alpha("#2563EB", 0.2)}`,
                }}
              />
            )}
          </Box>

          {/* ── Countries Card ── */}
          <SectionCard>
            <CardLabel>Select Country</CardLabel>

            {/* ── Skeleton ── */}
            {fetching ? (
              <Box>
                {[...Array(5)].map((_, i) => (
                  <SkeletonCard key={i} sx={{ animationDelay: `${i * 100}ms` }}>
                    <SkeletonBlock w={52} h={52} radius={13} />
                    <Box sx={{ flex: 1 }}>
                      <SkeletonBlock w="45%" h={15} radius={6} />
                    </Box>
                    <SkeletonBlock w={20} h={20} radius={4} />
                  </SkeletonCard>
                ))}
              </Box>
            ) : countries.length === 0 ? (
              /* ── Empty state ── */
              <Box sx={{ textAlign: "center", py: 6 }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 14,
                    background: alpha("#2563EB", 0.07),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 2,
                  }}
                >
                  <PublicIcon
                    sx={{ fontSize: 28, color: "#2563EB", opacity: 0.5 }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                  }}
                >
                  No countries found
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                    mt: 0.5,
                  }}
                >
                  Countries will appear here once added to the system
                </Typography>
              </Box>
            ) : (
              /* ── Country list ── */
              countries.map((country, i) => (
                <CountryCard
                  key={country.id}
                  itemdelay={i * 60}
                  onClick={() => navigateToExam(country.id)}
                >
                  {/* Flag */}
                  <FlagBox className="flag-box">
                    {country.flag ? (
                      <Box
                        component="img"
                        src={country.flag}
                        alt={`${country.name} flag`}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <PublicIcon
                        sx={{ fontSize: 24, color: "#2563EB", opacity: 0.45 }}
                      />
                    )}
                  </FlagBox>

                  {/* Name */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: 15,
                        fontWeight: 700,
                        color: "text.primary",
                        fontFamily: "'DM Serif Display', serif",
                        lineHeight: 1.2,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {country.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: "text.disabled",
                        fontFamily: "'Sora', sans-serif",
                        mt: 0.3,
                      }}
                    >
                      Click to view exams
                    </Typography>
                  </Box>

                  {/* Arrow */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.8,
                      background: alpha("#2563EB", 0.06),
                      border: `1px solid ${alpha("#2563EB", 0.15)}`,
                      borderRadius: 2.5,
                      px: 1.4,
                      py: 0.7,
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: "#2563EB",
                        fontFamily: "'Sora', sans-serif",
                      }}
                    >
                      View
                    </Typography>
                    <ArrowForwardIosIcon
                      className="arrow-icon"
                      sx={{
                        fontSize: 11,
                        color: "#2563EB",
                        transition: "transform 0.2s ease, opacity 0.2s ease",
                      }}
                    />
                  </Box>
                </CountryCard>
              ))
            )}
          </SectionCard>
        </ContentBox>
      </PageWrapper>
    </ThemeProvider>
  );
};

export default AdminExamCountryPage;
